import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "./Modal.js";
import { Icon } from "./Icon.js";
import { loadImageFile, cropToPortrait } from "../lib/image.js";

const VIEW = 300; // square preview size in px

interface Props {
  file: File;
  name: string;
  onSave: (dataUrl: string) => void;
  onClose: () => void;
}

/**
 * Square portrait cropper — load an image, pan (drag) and zoom to frame it inside a
 * circular viewport, then export an optimized square JPEG. Keeps non-square uploads
 * from being awkwardly center-cropped.
 */
export function ImageCropModal({ file, name, onSave, onClose }: Props) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadImageFile(file)
      .then((el) => { if (!cancelled) setImg(el); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Could not read image."); });
    return () => { cancelled = true; };
  }, [file]);

  const base = img ? Math.max(VIEW / img.naturalWidth, VIEW / img.naturalHeight) : 1;
  const eff = base * zoom;
  const drawW = img ? img.naturalWidth * eff : 0;
  const drawH = img ? img.naturalHeight * eff : 0;

  const clamp = useCallback((x: number, y: number) => ({
    x: Math.min(0, Math.max(VIEW - drawW, x)),
    y: Math.min(0, Math.max(VIEW - drawH, y))
  }), [drawW, drawH]);

  // Re-center / re-clamp when the image loads or zoom changes.
  useEffect(() => {
    if (!img) return;
    setPos((p) => clamp(p.x || (VIEW - drawW) / 2, p.y || (VIEW - drawH) / 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, zoom]);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, ox: pos.x, oy: pos.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos(clamp(drag.current.ox + (e.clientX - drag.current.x), drag.current.oy + (e.clientY - drag.current.y)));
  };
  const onPointerUp = () => { drag.current = null; };

  const save = () => {
    if (!img) return;
    const sx = -pos.x / eff;
    const sy = -pos.y / eff;
    const size = VIEW / eff;
    try { onSave(cropToPortrait(img, { sx, sy, size })); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not crop image."); }
  };

  return (
    <Modal
      title="Position portrait"
      subtitle={name}
      width={380}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!img}><Icon name="check" size={14} /> Save portrait</button>
        </>
      }
    >
      {error ? (
        <p className="intel-reason"><Icon name="alert" size={12} /> {error}</p>
      ) : (
        <div className="cropper">
          <div
            className="cropper-view"
            style={{ width: VIEW, height: VIEW }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {img && (
              <img
                className="cropper-img"
                src={img.src}
                alt=""
                draggable={false}
                style={{ width: drawW, height: drawH, transform: `translate(${pos.x}px, ${pos.y}px)` }}
              />
            )}
            <div className="cropper-mask" />
          </div>
          <label className="cropper-zoom">
            <Icon name="search" size={13} />
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </label>
          <p className="faint" style={{ fontSize: 11, margin: 0, textAlign: "center" }}>Drag to reposition · slide to zoom</p>
        </div>
      )}
    </Modal>
  );
}
